import React from "react";
import { createRoot } from "react-dom/client";
import { DEV_CONSOLE_PATH, isWorkspacesPage } from "@aquaduck/shared";
import { isPreviewPage } from "./lib/route";
import { DevBanner } from "./shell/DevBanner";
import { preventPageZoom } from "./lib/preventPageZoom";
import { installTooltips } from "./ui/tooltip";
import "./ui/tooltip.css";

const removePageZoomGuard = preventPageZoom(window);
if (import.meta.hot) import.meta.hot.dispose(removePageZoomGuard);
const removeTooltips = installTooltips();
if (import.meta.hot) import.meta.hot.dispose(removeTooltips);

async function boot() {
  if (new URLSearchParams(location.search).get("walkthrough") === "restart") {
    const { restartFromPreviewLink } = await import("./onboarding/restart");
    await restartFromPreviewLink();
  }
  const root = createRoot(document.getElementById("root")!);
  // Resolve public content before loading any workspace document, cache, or sync provider.
  const { accountRequest, refreshSession, AccountError } = await import("./account/session");
  const { rememberInvite, pendingInvite, forgetInvite } = await import("./account/pendingInvite");
  const path = location.pathname.replace(/\/$/, "") || "/";
  const isAppPath = path === "/app" || isWorkspacesPage(path) || /^\/(?:w|workspace|workspaces)\//.test(path) || path === "/preview" || path.startsWith("/auth/") || path === "/invite" || path === DEV_CONSOLE_PATH;
  let session;
  if (path === "/" && location.hash.startsWith("#tab=")) { location.replace(`/w/main${location.search}${location.hash}`); return; }
  if (path === "/") {
    try { session = await refreshSession(); } catch { /* The public page can still be displayed when account services are unavailable. */ }
  }
  if (!isAppPath && !(path === "/" && session?.user)) {
    try {
      const result = await accountRequest<{publication: {workspaceId: string}; snapshot: string}>(`/api/publications/resolve?path=${encodeURIComponent(location.pathname)}`);
      await renderPublication(result.snapshot, result.publication.workspaceId); return;
    } catch (error) {
      const missing = error instanceof AccountError && error.status === 404;
      if (path !== "/" || !missing) { await loadTheme(); root.render(<main className="publication-empty"><h1>{missing ? "Page not found" : "Page unavailable"}</h1><p>{missing ? "This page is unavailable or has been unpublished." : "We couldn’t load this page. Please try again."}</p><a href="/">Go to Aquaduck</a>{!missing && <button onClick={() => location.reload()}>Retry</button>}</main>); return; }
    }
  }
  let authError: string | undefined;
  try {
    if (path === "/auth/verify") {
      const token = new URLSearchParams(location.hash.slice(1)).get("token");
      history.replaceState(null, "", "/workspaces");
      if (!token) throw new Error("This sign-in link is missing its token. Request another link.");
      await accountRequest("/api/auth/verify", { token });
    }
    if (path === "/invite") {
      const token = new URLSearchParams(location.hash.slice(1)).get("token");
      history.replaceState(null, "", "/workspaces");
      if (token) rememberInvite(token);
    }
    session ??= await refreshSession();
    const invite = pendingInvite();
    if (invite && session.user) {
      const result = await accountRequest<{team: {url: string}}>("/api/teams/invites/accept", { token: invite });
      forgetInvite();
      location.assign(`${result.team.url.replace(/\/$/, "")}/workspaces`); return;
    }
  } catch (error) { authError = error instanceof Error ? error.message : "Could not sign in."; }
  if (!session || (session.enabled && (!session.user || !session.user.onboarded)) || authError) {
    const { AccountEntry } = await import("./account/AccountEntry");
    await loadTheme(); root.render(<React.StrictMode><AccountEntry session={session} error={authError} /></React.StrictMode>); return;
  }
  if (isPreviewPage(location.pathname)) {
    const { currentWorkspaceId } = await import("./lib/workspace");
    try {
      const id = currentWorkspaceId();
      const result = await accountRequest<{snapshot: string}>(`/api/workspaces/${encodeURIComponent(id)}/publication-preview`);
      await renderPublication(result.snapshot, id); return;
    } catch (error) { await loadTheme(); root.render(<main className="publication-empty"><h1>Preview unavailable</h1><p>{error instanceof Error ? error.message : "Could not load preview."}</p><a href="/workspaces">Back to workspaces</a></main>); return; }
  }
  if (isWorkspacesPage(location.pathname) || location.pathname === "/app" || location.pathname === "/") {
    const { WorkspacesPage } = await import("./workspaces/WorkspacesPage");
    await loadTheme();
    root.render(<React.StrictMode><div className="dev-page-shell"><DevBanner /><WorkspacesPage /></div></React.StrictMode>);
    return;
  }
  async function renderPublication(snapshot: string, workspaceId: string) {
    (window as Window & { __AQUADUCK_PUBLICATION__?: {snapshot: string; workspaceId: string} }).__AQUADUCK_PUBLICATION__ = { snapshot, workspaceId };
    const { PreviewPage } = await import("./present/PreviewPage");
    await loadTheme();
    root.render(<React.StrictMode><PreviewPage /></React.StrictMode>);
  }
  // Every workspace-dependent import stays behind the overview route boundary.
  const [{ WorkspaceGate }, { App }, { PreviewPage }, { DevConsole }] = await Promise.all([
    import("./workspaces/WorkspaceGate"),
    import("./App"), import("./present/PreviewPage"), import("./dev/DevConsole"),
  ]);
  await loadTheme();
  root.render(<React.StrictMode><WorkspaceGate>
    {location.pathname.replace(/\/$/, "") === DEV_CONSOLE_PATH ? <DevConsole /> : isPreviewPage(location.pathname) ? <div className="dev-page-shell"><DevBanner /><PreviewPage /></div> : <App />}
  </WorkspaceGate></React.StrictMode>);
}
async function loadTheme() {
  // Keep the shell and primitive overrides after feature CSS, as in the original boot.
  await import("./styles.css");
  await import("./ui/ui.css");
}
void boot();
